import { useState, useEffect, useCallback, useRef } from 'react';
import { BrainwaveControlPoint, ModulationType, BrainwaveOscillatorNode } from '../utils/audio/BrainwaveOscillatorNode';
import { NoiseSettings, WhiteNoiseGeneratorNode } from '../utils/audio/WhiteNoiseGeneratorNode';
import { AudioMixData, SerializableAudioTrack } from '../services/audioMixService';
import { AudioBufferSource, Output, Mp4OutputFormat, Mp3OutputFormat, BufferTarget, canEncodeAudio } from 'mediabunny';
import { registerMp3Encoder } from '@mediabunny/mp3-encoder';
import { config } from '../config';

export interface BrainwaveSettings {
  modulationType: ModulationType;
  controlPoints: BrainwaveControlPoint[];
  preset?: string;
  previewVolume?: number;
}

export interface AudioTrack {
  id: string;
  name: string;
  type: 'main' | 'music' | 'background' | 'sound' | 'brainwave' | 'noise';
  audioLibraryId?: string;
  audioFilePath?: string; // Path to the audio file (for library tracks)
  audioBuffer: AudioBuffer | null;
  gainNode: GainNode | null;
  sourceNode: AudioBufferSourceNode | null;
  volume: number;
  muted: boolean;
  startTime: number;
  duration: number;
  trimmedDuration?: number; // Optional trimmed duration for cutting tracks
  color: string;
  fadeIn?: number;
  fadeOut?: number;
  isLoaded: boolean;
  isPlaying: boolean;
  // Brainwave-specific settings
  brainwaveSettings?: BrainwaveSettings;
  brainwaveOscillator?: any; // Will be BrainwaveOscillatorNode instance
  // Noise-specific settings
  noiseSettings?: NoiseSettings;
  noiseGenerator?: any; // Will be WhiteNoiseGeneratorNode instance
}

export interface WebAudioState {
  audioContext: AudioContext | null;
  masterGainNode: GainNode | null;
  analyserNode: AnalyserNode | null;
  isInitialized: boolean;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  tracks: AudioTrack[];
  masterVolume: number;
  isLooping: boolean;
}

export interface WebAudioControls {
  initializeAudio: () => Promise<void>;
  addTrack: (track: Omit<AudioTrack, 'audioBuffer' | 'gainNode' | 'sourceNode' | 'isLoaded' | 'isPlaying'>) => void;
  loadTrackAudio: (trackId: string, audioUrl: string) => Promise<void>;
  loadTrackAudioBuffer: (trackId: string, audioBuffer: AudioBuffer) => void;
  removeTrack: (trackId: string) => void;
  updateTrack: (trackId: string, updates: Partial<AudioTrack>) => void;
  setMasterVolume: (volume: number) => void;
  play: () => void;
  pause: () => void;
  stop: () => void;
  seek: (time: number) => void;
  toggleLoop: () => void;
  getFrequencyData: () => Uint8Array;
  getWaveformData: () => Uint8Array;
  exportMixData: () => AudioMixData;
  importMixData: (mixData: AudioMixData, scriptAudioPath?: string, apiBaseUrl?: string) => Promise<void>;
  renderMixToFile: (onProgress?: (stage: 'rendering' | 'encoding', progress: number) => void) => Promise<Blob>;
}

export const useWebAudio = (): WebAudioState & WebAudioControls => {
  const [audioContext, setAudioContext] = useState<AudioContext | null>(null);
  const [masterGainNode, setMasterGainNode] = useState<GainNode | null>(null);
  const [analyserNode, setAnalyserNode] = useState<AnalyserNode | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [tracks, setTracks] = useState<AudioTrack[]>([]);
  const [masterVolume, setMasterVolumeState] = useState(1);
  const [isLooping, setIsLooping] = useState(false);

  const animationFrameRef = useRef<number>();
  const timeUpdateIntervalRef = useRef<NodeJS.Timeout>();
  const startTimeRef = useRef<number>(0);
  const pauseTimeRef = useRef<number>(0);
  const isPlayingRef = useRef<boolean>(false);
  const activeSourceNodesRef = useRef<Map<string, AudioBufferSourceNode>>(new Map());
  const activeBrainwaveOscillatorsRef = useRef<Map<string, BrainwaveOscillatorNode>>(new Map());
  const activeNoiseGeneratorsRef = useRef<Map<string, WhiteNoiseGeneratorNode>>(new Map());
  const playRef = useRef<(() => void) | null>(null);
  const seekRef = useRef<((time: number) => void) | null>(null);

  // Helper function to calculate total duration based on all tracks
  const calculateTotalDuration = useCallback((tracks: AudioTrack[]) => {
    if (tracks.length === 0) return 0;
    
    return Math.max(...tracks.map(track => 
      track.isLoaded ? (track.startTime || 0) + (track.trimmedDuration || track.duration) : 0
    ));
  }, []);

  // Initialize Web Audio Context
  const initializeAudio = useCallback(async () => {
    if (audioContext) return;

    try {
      const context = new (window.AudioContext || (window as any).webkitAudioContext)();
      
      // Create master gain node
      const masterGain = context.createGain();
      masterGain.connect(context.destination);
      
      // Create analyser node for visualization
      const analyser = context.createAnalyser();
      analyser.fftSize = 2048;
      analyser.connect(masterGain);
      
      setAudioContext(context);
      setMasterGainNode(masterGain);
      setAnalyserNode(analyser);
      setIsInitialized(true);
      
      console.log('Web Audio API initialized successfully');
    } catch (error) {
      console.error('Failed to initialize Web Audio API:', error);
    }
  }, [audioContext]);

  // Update current time during playback (throttled for better performance)

  const updateCurrentTime = useCallback(() => {
    if (!audioContext || !isPlayingRef.current) return;

    const elapsed = pauseTimeRef.current + (audioContext.currentTime - startTimeRef.current);
    setCurrentTime(elapsed);

    if (elapsed >= duration-0.1 && duration > 0) {
      // Audio has reached the end
      console.log('Audio playback completed, elapsed:', elapsed, 'duration:', duration);
      if (isLooping) {
        // For looping, use seek to jump back to start
        console.log('Audio playback completed, looping back to start');
        
        // Schedule seek in next animation frame to ensure smooth transition
        requestAnimationFrame(() => {
          if (seekRef.current && isPlayingRef.current) {
            seekRef.current(0.01);
          }
        });
      } else {
        // Stop playback
        console.log('Audio playback completed, resetting state');
        
        // Stop all active noise generators
        activeNoiseGeneratorsRef.current.forEach((generator, trackId) => {
          console.log(`Audio completed: Stopping noise generator for track ${trackId}`);
          try {
            generator.stop();
            generator.disconnect();
          } catch (error) {
            console.error(`Error stopping noise generator for track ${trackId}:`, error);
          }
        });
        activeNoiseGeneratorsRef.current.clear();
        
        // Stop all active brainwave oscillators
        activeBrainwaveOscillatorsRef.current.forEach((oscillator, trackId) => {
          console.log(`Audio completed: Stopping brainwave oscillator for track ${trackId}`);
          try {
            oscillator.stop();
            oscillator.disconnect();
          } catch (error) {
            console.error(`Error stopping brainwave oscillator for track ${trackId}:`, error);
          }
        });
        activeBrainwaveOscillatorsRef.current.clear();
        
        setIsPlaying(false);
        isPlayingRef.current = false;
        setCurrentTime(0);
        pauseTimeRef.current = 0;
        startTimeRef.current = 0;
        
        // Clear active source nodes
        activeSourceNodesRef.current.clear();
        
        // Clear the update interval
        if (timeUpdateIntervalRef.current) {
          clearInterval(timeUpdateIntervalRef.current);
          timeUpdateIntervalRef.current = undefined;
        }
      }
      
      // Update tracks state
      setTracks(prev => prev.map(track => ({
        ...track,
        sourceNode: null,
        brainwaveOscillator: null,
        noiseGenerator: null,
        isPlaying: false
      })));
    }
  }, [audioContext, duration, isLooping]);

  // Start time updates when playing (throttled to 10fps)
  useEffect(() => {
    if (isPlaying) {
      // Clear any existing interval
      if (timeUpdateIntervalRef.current) {
        clearInterval(timeUpdateIntervalRef.current);
      }
      // Start throttled updates at 00fps (33.33ms intervals)
      timeUpdateIntervalRef.current = setInterval(updateCurrentTime, 33.33);
    } else {
      if (timeUpdateIntervalRef.current) {
        clearInterval(timeUpdateIntervalRef.current);
        timeUpdateIntervalRef.current = undefined;
      }
    }

    return () => {
      if (timeUpdateIntervalRef.current) {
        clearInterval(timeUpdateIntervalRef.current);
        timeUpdateIntervalRef.current = undefined;
      }
    };
  }, [isPlaying, updateCurrentTime]);

  // Add a new track
  const addTrack = useCallback((track: Omit<AudioTrack, 'audioBuffer' | 'gainNode' | 'sourceNode' | 'isLoaded' | 'isPlaying'>) => {
    let gainNode: GainNode | null = null;
    let isLoaded = false;

    // For brainwave and noise tracks, create gain node immediately
    if ((track.type === 'brainwave' || track.type === 'noise') && audioContext) {
      gainNode = audioContext.createGain();
      gainNode.connect(analyserNode || audioContext.destination);
      isLoaded = true; // Brainwave and noise tracks are "loaded" immediately
    }

    const newTrack: AudioTrack = {
      ...track,
      audioBuffer: null,
      gainNode,
      sourceNode: null,
      isLoaded,
      isPlaying: false
    };

    setTracks(prev => {
      const updatedTracks = [...prev, newTrack];
      
      // Update total duration for brainwave and noise tracks
      if (track.type === 'brainwave' || track.type === 'noise') {
        const newDuration = calculateTotalDuration(updatedTracks);
        setDuration(newDuration);
      }
      
      return updatedTracks;
    });
  }, [audioContext, analyserNode, calculateTotalDuration]);

  // Load audio for a track
  const loadTrackAudio = useCallback(async (trackId: string, audioUrl: string) => {
    if (!audioContext) {
      console.error('Audio context not initialized');
      return;
    }

    try {
      console.log(`Loading audio from URL: ${audioUrl}`);
      const response = await fetch(audioUrl);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const arrayBuffer = await response.arrayBuffer();
      console.log(`Audio buffer size: ${arrayBuffer.byteLength} bytes`);
      
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

      // Create gain node for this track
      const gainNode = audioContext.createGain();
      gainNode.connect(analyserNode || audioContext.destination);

      setTracks(prev => {
        const updatedTracks = prev.map(track => 
          track.id === trackId 
            ? { 
                ...track, 
                audioBuffer, 
                gainNode, 
                isLoaded: true,
                duration: audioBuffer.duration
              }
            : track
        );
        
        // Update total duration based on all tracks
        const newDuration = calculateTotalDuration(updatedTracks);
        setDuration(newDuration);
        
        return updatedTracks;
      });

      console.log(`Loaded audio for track ${trackId}, duration: ${audioBuffer.duration}s`);
    } catch (error) {
      console.error(`Failed to load audio for track ${trackId}:`, error);
    }
  }, [audioContext, analyserNode, calculateTotalDuration]);

  // Load audio buffer directly for a track (e.g., generated audio)
  const loadTrackAudioBuffer = useCallback((trackId: string, audioBuffer: AudioBuffer) => {
    if (!audioContext) {
      console.error('Audio context not initialized');
      return;
    }

    try {
      // Create gain node for this track
      const gainNode = audioContext.createGain();
      gainNode.connect(analyserNode || audioContext.destination);

      setTracks(prev => {
        const updatedTracks = prev.map(track => 
          track.id === trackId 
            ? { 
                ...track, 
                audioBuffer, 
                gainNode, 
                isLoaded: true,
                duration: audioBuffer.duration
              }
            : track
        );
        
        // Update total duration based on all tracks
        const newDuration = calculateTotalDuration(updatedTracks);
        setDuration(newDuration);
        
        return updatedTracks;
      });

      console.log(`Loaded audio buffer for track ${trackId}, duration: ${audioBuffer.duration}s`);
    } catch (error) {
      console.error(`Failed to load audio buffer for track ${trackId}:`, error);
    }
  }, [audioContext, analyserNode, calculateTotalDuration]);

  // Remove a track
  const removeTrack = useCallback((trackId: string) => {
    setTracks(prev => {
      const track = prev.find(t => t.id === trackId);
      if (track?.sourceNode) {
        track.sourceNode.stop();
      }
      if (track?.gainNode) {
        track.gainNode.disconnect();
      }
      const updatedTracks = prev.filter(t => t.id !== trackId);
      
      // Recalculate total duration after removing track
      const newDuration = calculateTotalDuration(updatedTracks);
      setDuration(newDuration);
      
      return updatedTracks;
    });
  }, [calculateTotalDuration]);

  // Update track properties
  const updateTrack = useCallback((trackId: string, updates: Partial<AudioTrack>) => {
    setTracks(prev => {
      const updatedTracks = prev.map(track => {
        if (track.id !== trackId) return track;

        const updatedTrack = { ...track, ...updates };

        // Update gain node if volume or muted changed
        if (track.gainNode && ('volume' in updates || 'muted' in updates)) {
          const volume = updatedTrack.muted ? 0 : updatedTrack.volume;
          track.gainNode.gain.value = volume;
          
          // For brainwave tracks, also update the oscillator if it's currently playing
          if (track.type === 'brainwave') {
            const oscillator = activeBrainwaveOscillatorsRef.current.get(track.id);
            if (oscillator) {
              oscillator.setTrackVolume(volume);
            }
          }
        }

        return updatedTrack;
      });
      
      // Recalculate total duration if startTime or trimmedDuration was updated
      if ('startTime' in updates || 'trimmedDuration' in updates) {
        const newDuration = calculateTotalDuration(updatedTracks);
        setDuration(newDuration);
      }
      
      return updatedTracks;
    });
  }, [calculateTotalDuration]);

  // Set master volume
  const setMasterVolume = useCallback((volume: number) => {
    if (masterGainNode) {
      masterGainNode.gain.value = volume;
    }
    setMasterVolumeState(volume);
  }, [masterGainNode]);

  // Play all tracks
  const play = useCallback(() => {
    console.log('PLAY: Function called, stack trace:', new Error().stack);
    if (!audioContext || !isInitialized) {
      console.log('PLAY: Returning early - no audio context or not initialized');
      return;
    }

    // Prevent multiple simultaneous play calls
    if (isPlayingRef.current) {
      console.log('PLAY: Already playing, ignoring duplicate call');
      return;
    }

    isPlayingRef.current = true;

    // Resume audio context if suspended
    if (audioContext.state === 'suspended') {
      console.log('PLAY: Resuming suspended audio context');
      audioContext.resume();
    }

    const loadedTracks = tracks.filter(track => track.isLoaded && track.audioBuffer);
    const brainwaveTracks = tracks.filter(track => track.type === 'brainwave' && track.brainwaveSettings);
    const noiseTracks = tracks.filter(track => track.type === 'noise' && track.noiseSettings);
    
    console.log('PLAY: Function called with tracks:', tracks.map(t => ({ 
      id: t.id, 
      name: t.name, 
      type: t.type,
      isLoaded: t.isLoaded, 
      hasAudioBuffer: !!t.audioBuffer,
      hasSourceNode: !!t.sourceNode,
      hasBrainwaveSettings: !!t.brainwaveSettings
    })));
    
    if (loadedTracks.length === 0 && brainwaveTracks.length === 0 && noiseTracks.length === 0) {
      console.warn('PLAY: No loaded tracks, brainwave tracks, or noise tracks to play');
      return;
    }

    // Stop any existing source nodes
    console.log('PLAY: Stopping existing source nodes');
    activeSourceNodesRef.current.forEach((sourceNode, trackId) => {
      console.log(`PLAY: Stopping existing source node for track ${trackId}`);
      try {
        sourceNode.stop();
        console.log(`PLAY: Successfully stopped existing source node for track ${trackId}`);
      } catch (error) {
        console.error(`PLAY: Error stopping existing source node for track ${trackId}:`, error);
      }
    });
    activeSourceNodesRef.current.clear();

    // Stop any existing brainwave oscillators
    activeBrainwaveOscillatorsRef.current.forEach((oscillator, trackId) => {
      console.log(`PLAY: Stopping existing brainwave oscillator for track ${trackId}`);
      try {
        oscillator.stop();
        oscillator.disconnect();
      } catch (error) {
        console.error(`PLAY: Error stopping brainwave oscillator for track ${trackId}:`, error);
      }
    });
    activeBrainwaveOscillatorsRef.current.clear();

    // Stop any existing noise generators
    activeNoiseGeneratorsRef.current.forEach((generator, trackId) => {
      console.log(`PLAY: Stopping existing noise generator for track ${trackId}`);
      try {
        generator.stop();
        generator.disconnect();
      } catch (error) {
        console.error(`PLAY: Error stopping noise generator for track ${trackId}:`, error);
      }
    });
    activeNoiseGeneratorsRef.current.clear();

    // Create new source nodes and start playback
    const regularTracks = loadedTracks.filter(track => track.type !== 'brainwave' && track.type !== 'noise' && track.audioBuffer && track.gainNode);
    
    console.log(`PLAY: Creating ${regularTracks.length} source nodes, ${brainwaveTracks.length} brainwave oscillators, and ${noiseTracks.length} noise generators`);
    
    // Handle regular audio tracks
    regularTracks.forEach(track => {
      console.log(`PLAY: Creating new source node for track ${track.id} (${track.name})`);
      const sourceNode = audioContext.createBufferSource();
      sourceNode.buffer = track.audioBuffer!;
      sourceNode.connect(track.gainNode!);

      // Set up initial volume (will be overridden by fade if needed)
      const baseVolume = track.muted ? 0 : track.volume;
      track.gainNode!.gain.value = baseVolume;

      // Store the source node in our ref
      activeSourceNodesRef.current.set(track.id, sourceNode);

      // Calculate timing based on track's startTime and current playback position
      const currentTimelinePosition = pauseTimeRef.current;
      const trackStartTime = track.startTime || 0;
      const effectiveDuration = track.trimmedDuration || track.duration;
      const trackEndTime = trackStartTime + effectiveDuration;
      
      // Check if track should be playing at current timeline position
      if (currentTimelinePosition >= trackStartTime && currentTimelinePosition < trackEndTime) {
        // Track should be playing - calculate offset within the track
        const trackOffset = currentTimelinePosition - trackStartTime;
        console.log(`PLAY: Starting playback for track ${track.id} from timeline position ${currentTimelinePosition}s, track offset ${trackOffset}s, volume: ${baseVolume}`);
        
        // Apply fade in if we're within the fade duration
        if (track.fadeIn && trackOffset < track.fadeIn) {
          const fadeProgress = trackOffset / track.fadeIn;
          const currentVolume = baseVolume * fadeProgress;
          track.gainNode!.gain.setValueAtTime(currentVolume, audioContext.currentTime);
          track.gainNode!.gain.linearRampToValueAtTime(baseVolume, audioContext.currentTime + (track.fadeIn - trackOffset));
        }
        
        // Apply fade out if needed
        if (track.fadeOut && track.fadeOut > 0) {
          const fadeOutStart = effectiveDuration - track.fadeOut;
          if (trackOffset < fadeOutStart) {
            // Schedule fade out for later
            const timeUntilFadeOut = fadeOutStart - trackOffset;
            track.gainNode!.gain.setValueAtTime(baseVolume, audioContext.currentTime + timeUntilFadeOut);
            track.gainNode!.gain.linearRampToValueAtTime(0, audioContext.currentTime + timeUntilFadeOut + track.fadeOut);
          } else {
            // We're already in the fade out region
            const fadeOutProgress = (trackOffset - fadeOutStart) / track.fadeOut;
            const currentVolume = baseVolume * (1 - fadeOutProgress);
            track.gainNode!.gain.setValueAtTime(currentVolume, audioContext.currentTime);
            track.gainNode!.gain.linearRampToValueAtTime(0, audioContext.currentTime + (effectiveDuration - trackOffset));
          }
        }
        
        // If trimmed, schedule stop at the trimmed duration
        if (track.trimmedDuration && track.trimmedDuration < track.duration) {
          const remainingDuration = track.trimmedDuration - trackOffset;
          sourceNode.start(0, trackOffset, remainingDuration);
        } else {
          sourceNode.start(0, trackOffset);
        }
      } else if (currentTimelinePosition < trackStartTime) {
        // Track starts in the future - schedule it to start at the correct time
        const delayTime = trackStartTime - currentTimelinePosition;
        const startTimeInContext = audioContext.currentTime + delayTime;
        console.log(`PLAY: Scheduling track ${track.id} to start in ${delayTime}s, volume: ${baseVolume}`);
        
        // Schedule fade in
        if (track.fadeIn && track.fadeIn > 0) {
          track.gainNode!.gain.setValueAtTime(0, startTimeInContext);
          track.gainNode!.gain.linearRampToValueAtTime(baseVolume, startTimeInContext + track.fadeIn);
        } else {
          track.gainNode!.gain.setValueAtTime(baseVolume, startTimeInContext);
        }
        
        // Schedule fade out
        if (track.fadeOut && track.fadeOut > 0) {
          const fadeOutStart = startTimeInContext + effectiveDuration - track.fadeOut;
          track.gainNode!.gain.setValueAtTime(baseVolume, fadeOutStart);
          track.gainNode!.gain.linearRampToValueAtTime(0, fadeOutStart + track.fadeOut);
        }
        
        // If trimmed, limit duration
        if (track.trimmedDuration && track.trimmedDuration < track.duration) {
          sourceNode.start(audioContext.currentTime + delayTime, 0, track.trimmedDuration);
        } else {
          sourceNode.start(audioContext.currentTime + delayTime, 0);
        }
      } else {
        // Track has already ended - don't play it
        console.log(`PLAY: Track ${track.id} has already ended, not playing`);
        activeSourceNodesRef.current.delete(track.id);
      }
    });

    // Handle brainwave tracks
    brainwaveTracks.forEach(track => {
      if (!track.gainNode || !track.brainwaveSettings) return;
      
      console.log(`PLAY: Creating new brainwave oscillator for track ${track.id} (${track.name})`);
      
      // Create gain node if not exists
      if (!track.gainNode) {
        const gainNode = audioContext.createGain();
        gainNode.connect(analyserNode || audioContext.destination);
        track.gainNode = gainNode;
      }
      
      const effectiveDuration = track.trimmedDuration || track.duration;
      const oscillator = new BrainwaveOscillatorNode({
        audioContext,
        modulationType: track.brainwaveSettings.modulationType,
        controlPoints: track.brainwaveSettings.controlPoints,
        duration: effectiveDuration,
        trackVolume: track.muted ? 0 : track.volume
      });
      
      oscillator.connect(track.gainNode);
      
      // Store the oscillator
      activeBrainwaveOscillatorsRef.current.set(track.id, oscillator);
      
      // Calculate timing
      const currentTimelinePosition = pauseTimeRef.current;
      const trackStartTime = track.startTime || 0;
      const trackEndTime = trackStartTime + effectiveDuration;
      
      // Check if track should be playing
      if (currentTimelinePosition >= trackStartTime && currentTimelinePosition < trackEndTime) {
        const trackOffset = currentTimelinePosition - trackStartTime;
        console.log(`PLAY: Starting brainwave oscillator for track ${track.id} from offset ${trackOffset}s`);
        oscillator.start(audioContext.currentTime, trackOffset);
      } else if (currentTimelinePosition < trackStartTime) {
        const delayTime = trackStartTime - currentTimelinePosition;
        console.log(`PLAY: Scheduling brainwave oscillator for track ${track.id} to start in ${delayTime}s`);
        oscillator.start(audioContext.currentTime + delayTime, 0);
      } else {
        console.log(`PLAY: Brainwave track ${track.id} has already ended, not playing`);
        activeBrainwaveOscillatorsRef.current.delete(track.id);
      }
    });

    // Handle noise tracks
    noiseTracks.forEach(track => {
      if (!track.gainNode || !track.noiseSettings) return;
      
      console.log(`PLAY: Creating new noise generator for track ${track.id} (${track.name})`);
      
      // Create gain node if not exists
      if (!track.gainNode) {
        const gainNode = audioContext.createGain();
        gainNode.connect(analyserNode || audioContext.destination);
        track.gainNode = gainNode;
      }
      
      const effectiveDuration = track.trimmedDuration || track.duration;
      const generator = new WhiteNoiseGeneratorNode(audioContext, {
        ...track.noiseSettings,
        duration: effectiveDuration
      });
      
      generator.connect(track.gainNode);
      const baseVolume = track.muted ? 0 : track.volume;
      generator.setVolume(baseVolume);
      
      // Store the generator
      activeNoiseGeneratorsRef.current.set(track.id, generator);
      
      // Calculate timing
      const currentTimelinePosition = pauseTimeRef.current;
      const trackStartTime = track.startTime || 0;
      const trackEndTime = trackStartTime + effectiveDuration;
      
      // Check if track should be playing
      if (currentTimelinePosition >= trackStartTime && currentTimelinePosition < trackEndTime) {
        const trackOffset = currentTimelinePosition - trackStartTime;
        console.log(`PLAY: Starting noise generator for track ${track.id} from offset ${trackOffset}s`);
        
        generator.start(audioContext.currentTime).catch(error => {
          console.error(`Failed to start noise generator for track ${track.id}:`, error);
        });
        
        // Apply fade in if we're within the fade duration
        if (track.fadeIn && trackOffset < track.fadeIn) {
          const fadeProgress = trackOffset / track.fadeIn;
          const currentVolume = baseVolume * fadeProgress;
          generator.setVolume(currentVolume);
          const remainingFadeTime = track.fadeIn - trackOffset;
          generator.scheduleFadeIn(remainingFadeTime, baseVolume, audioContext.currentTime);
        }
        
        // Apply fade out if needed
        if (track.fadeOut && track.fadeOut > 0) {
          const fadeOutStart = effectiveDuration - track.fadeOut;
          if (trackOffset < fadeOutStart) {
            // Schedule fade out for later
            const timeUntilFadeOut = fadeOutStart - trackOffset;
            generator.scheduleFadeOut(track.fadeOut, audioContext.currentTime + timeUntilFadeOut, baseVolume);
          } else {
            // We're already in the fade out region
            const fadeOutProgress = (trackOffset - fadeOutStart) / track.fadeOut;
            const currentVolume = baseVolume * (1 - fadeOutProgress);
            generator.setVolume(currentVolume);
            const remainingFadeTime = effectiveDuration - trackOffset;
            generator.scheduleFadeOut(remainingFadeTime, audioContext.currentTime, currentVolume);
          }
        }
      } else if (currentTimelinePosition < trackStartTime) {
        const delayTime = trackStartTime - currentTimelinePosition;
        console.log(`PLAY: Scheduling noise generator for track ${track.id} to start in ${delayTime}s`);
        
        generator.start(audioContext.currentTime + delayTime).catch(error => {
          console.error(`Failed to start noise generator for track ${track.id}:`, error);
        });
        
        // Schedule fade in
        if (track.fadeIn && track.fadeIn > 0) {
          generator.scheduleFadeIn(track.fadeIn, baseVolume, audioContext.currentTime + delayTime);
        } else {
          // Set initial volume immediately
          generator.setVolume(baseVolume);
        }
        
        // Schedule fade out
        if (track.fadeOut && track.fadeOut > 0) {
          const fadeOutStart = audioContext.currentTime + delayTime + effectiveDuration - track.fadeOut;
          generator.scheduleFadeOut(track.fadeOut, fadeOutStart, baseVolume);
        }
      } else {
        console.log(`PLAY: Noise track ${track.id} has already ended, not playing`);
        activeNoiseGeneratorsRef.current.delete(track.id);
      }
    });

    // Update tracks state only once
    setTracks(prev => prev.map(track => {
      if (track.type === 'brainwave') {
        return {
          ...track,
          brainwaveOscillator: activeBrainwaveOscillatorsRef.current.get(track.id) || null,
          isPlaying: activeBrainwaveOscillatorsRef.current.has(track.id)
        };
      } else if (track.type === 'noise') {
        return {
          ...track,
          noiseGenerator: activeNoiseGeneratorsRef.current.get(track.id) || null,
          isPlaying: activeNoiseGeneratorsRef.current.has(track.id)
        };
      }
      return {
        ...track,
        sourceNode: activeSourceNodesRef.current.get(track.id) || null,
        isPlaying: activeSourceNodesRef.current.has(track.id)
      };
    }));

    startTimeRef.current = audioContext.currentTime;
    setIsPlaying(true);
  }, [audioContext, isInitialized, tracks, currentTime]);
  
  // Store play function in ref for loop functionality
  playRef.current = play;

  // Pause playback
  const pause = useCallback(() => {
    console.log('PAUSE: Starting pause function');
    console.log('PAUSE: Active source nodes:', Array.from(activeSourceNodesRef.current.keys()));
    
    // Stop all source nodes using our ref
    activeSourceNodesRef.current.forEach((sourceNode, trackId) => {
      console.log(`PAUSE: Stopping source node for track ${trackId}`);
      try {
        sourceNode.stop();
        console.log(`PAUSE: Successfully stopped source node for track ${trackId}`);
      } catch (error) {
        console.error(`PAUSE: Error stopping source node for track ${trackId}:`, error);
      }
    });
    
    // Clear the ref
    activeSourceNodesRef.current.clear();

    // Stop all brainwave oscillators
    activeBrainwaveOscillatorsRef.current.forEach((oscillator, trackId) => {
      console.log(`PAUSE: Stopping brainwave oscillator for track ${trackId}`);
      try {
        oscillator.stop();
        oscillator.disconnect();
      } catch (error) {
        console.error(`PAUSE: Error stopping brainwave oscillator for track ${trackId}:`, error);
      }
    });
    activeBrainwaveOscillatorsRef.current.clear();

    // Stop all noise generators
    activeNoiseGeneratorsRef.current.forEach((generator, trackId) => {
      console.log(`PAUSE: Stopping noise generator for track ${trackId}`);
      try {
        generator.stop();
        generator.disconnect();
      } catch (error) {
        console.error(`PAUSE: Error stopping noise generator for track ${trackId}:`, error);
      }
    });
    activeNoiseGeneratorsRef.current.clear();

    // Update tracks state
    setTracks(prev => prev.map(track => ({
      ...track,
      sourceNode: null,
      brainwaveOscillator: null,
      noiseGenerator: null,
      isPlaying: false
    })));

    pauseTimeRef.current = currentTime;
    setIsPlaying(false);
    isPlayingRef.current = false;
    console.log('PAUSE: Pause function completed');
  }, [currentTime]);

  // Stop playback
  const stop = useCallback(() => {
    console.log('STOP: Starting stop function');
    console.log('STOP: Active source nodes:', Array.from(activeSourceNodesRef.current.keys()));
    
    // Stop all source nodes using our ref
    activeSourceNodesRef.current.forEach((sourceNode, trackId) => {
      console.log(`STOP: Stopping source node for track ${trackId}`);
      try {
        sourceNode.stop();
        console.log(`STOP: Successfully stopped source node for track ${trackId}`);
      } catch (error) {
        console.error(`STOP: Error stopping source node for track ${trackId}:`, error);
      }
    });
    
    // Clear the ref
    activeSourceNodesRef.current.clear();

    // Stop all brainwave oscillators
    activeBrainwaveOscillatorsRef.current.forEach((oscillator, trackId) => {
      console.log(`STOP: Stopping brainwave oscillator for track ${trackId}`);
      try {
        oscillator.stop();
        oscillator.disconnect();
      } catch (error) {
        console.error(`STOP: Error stopping brainwave oscillator for track ${trackId}:`, error);
      }
    });
    activeBrainwaveOscillatorsRef.current.clear();

    // Stop all noise generators
    activeNoiseGeneratorsRef.current.forEach((generator, trackId) => {
      console.log(`STOP: Stopping noise generator for track ${trackId}`);
      try {
        generator.stop();
        generator.disconnect();
      } catch (error) {
        console.error(`STOP: Error stopping noise generator for track ${trackId}:`, error);
      }
    });
    activeNoiseGeneratorsRef.current.clear();

    // Update tracks state
    setTracks(prev => prev.map(track => ({
      ...track,
      sourceNode: null,
      brainwaveOscillator: null,
      isPlaying: false
    })));

    setIsPlaying(false);
    setCurrentTime(0);
    pauseTimeRef.current = 0;
    startTimeRef.current = 0;
    isPlayingRef.current = false;
    console.log('STOP: Stop function completed');
  }, []);

  // Seek to specific time
  const seek = useCallback((time: number) => {
    console.log(`SEEK: Seeking to time ${time}s`);
    const wasPlaying = isPlaying;
    
    // Always stop playback first
    if (isPlaying) {
      pause();
    }
    
    // Update time and pause position immediately
    const clampedTime = Math.max(0, Math.min(duration || Infinity, time));
    setCurrentTime(clampedTime);
    pauseTimeRef.current = clampedTime;
    
    console.log(`SEEK: Set pauseTimeRef to ${clampedTime}s`);
    
    // If we were playing, restart from new position
    if (wasPlaying) {
      // Use requestAnimationFrame for smoother timing
      requestAnimationFrame(() => {
        console.log(`SEEK: Restarting playback from ${clampedTime}s`);
        play();
      });
    }
  }, [isPlaying, pause, play, duration]);

  // Toggle loop mode
  const toggleLoop = useCallback(() => {
    setIsLooping(prev => !prev);
  }, []);
  
  // Store seek function in ref for use in updateCurrentTime
  useEffect(() => {
    seekRef.current = seek;
  }, [seek]);

  // Get frequency data for visualization
  const getFrequencyData = useCallback((): Uint8Array => {
    if (!analyserNode) return new Uint8Array(0);
    
    const dataArray = new Uint8Array(analyserNode.frequencyBinCount);
    analyserNode.getByteFrequencyData(dataArray);
    return dataArray;
  }, [analyserNode]);

  // Get waveform data for visualization
  const getWaveformData = useCallback((): Uint8Array => {
    if (!analyserNode) return new Uint8Array(0);
    
    const dataArray = new Uint8Array(analyserNode.frequencyBinCount);
    analyserNode.getByteTimeDomainData(dataArray);
    return dataArray;
  }, [analyserNode]);

  // Export current audio mix state to serializable format
  const exportMixData = useCallback((): AudioMixData => {
    const serializableTracks: SerializableAudioTrack[] = tracks.map(track => ({
      id: track.id,
      name: track.name,
      type: track.type,
      audioLibraryId: track.audioLibraryId,
      audioFilePath: track.audioFilePath,
      volume: track.volume,
      muted: track.muted,
      startTime: track.startTime,
      duration: track.duration,
      trimmedDuration: track.trimmedDuration,
      color: track.color,
      fadeIn: track.fadeIn,
      fadeOut: track.fadeOut,
      brainwaveSettings: track.brainwaveSettings,
      noiseSettings: track.noiseSettings
    }));

    return {
      version: '1.0.0',
      masterVolume,
      totalDuration: duration,
      tracks: serializableTracks,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
  }, [tracks, masterVolume, duration]);

  // Import audio mix data and restore state
  const importMixData = useCallback(async (mixData: AudioMixData, scriptAudioPath?: string, apiBaseUrl?: string): Promise<void> => {
    if (!audioContext || !isInitialized) {
      console.error('Cannot import mix data: audio context not initialized');
      return;
    }

    try {
      console.log('Importing audio mix data...', mixData);

      // Stop current playback
      if (isPlaying) {
        stop();
      }

      // Clear current tracks
      setTracks([]);

      // Set master volume
      setMasterVolume(mixData.masterVolume);

      // Set total duration
      setDuration(mixData.totalDuration);

      // Restore tracks
      const restoredTracks: AudioTrack[] = [];

      for (const serializedTrack of mixData.tracks) {
        console.log(`Restoring track: ${serializedTrack.name} (${serializedTrack.type})`);

        let gainNode: GainNode | null = null;
        let audioBuffer: AudioBuffer | null = null;
        let isLoaded = false;

        // Create gain node
        gainNode = audioContext.createGain();
        gainNode.connect(analyserNode || audioContext.destination);
        gainNode.gain.value = serializedTrack.muted ? 0 : serializedTrack.volume;

        // Handle different track types
        if (serializedTrack.type === 'brainwave') {
          // Brainwave tracks are immediately "loaded"
          isLoaded = true;
        } else if (serializedTrack.type === 'noise') {
          // Noise tracks are immediately "loaded"
          isLoaded = true;
        } else if (serializedTrack.type === 'main') {
          // Main track - reload from script audio file if provided
          if (scriptAudioPath) {
            try {
              console.log(`Loading main track from: ${scriptAudioPath}`);
              const response = await fetch(scriptAudioPath);
              if (response.ok) {
                const arrayBuffer = await response.arrayBuffer();
                audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
                isLoaded = true;
                console.log(`Loaded main track: ${serializedTrack.name}`);
              } else {
                console.warn(`Failed to load main track: ${scriptAudioPath}`);
              }
            } catch (error) {
              console.error(`Error loading main track ${serializedTrack.name}:`, error);
            }
          } else {
            console.log('Main track restoration: no script audio path provided');
          }
        } else if (serializedTrack.audioLibraryId || serializedTrack.audioFilePath) {
          // Audio library track - load by file path or ID
          try {
            // Use S3 URL for audio library items (audioFilePath is now the S3 key)
            const audioUrl = serializedTrack.audioFilePath 
              ? `https://${config.awsS3LibraryBucket}.s3.amazonaws.com/${serializedTrack.audioFilePath}`
              : null; // Can't restore without the S3 key
            
            if (!audioUrl) {
              console.error(`Cannot restore audio library track ${serializedTrack.name}: no S3 key available`);
              continue;
            }
            
            console.log(`Loading audio library track from S3: ${audioUrl}`);
            
            const response = await fetch(audioUrl);
            if (response.ok) {
              const arrayBuffer = await response.arrayBuffer();
              audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
              isLoaded = true;
              console.log(`Loaded audio library track: ${serializedTrack.name}`);
            } else {
              console.warn(`Failed to load audio library track: ${serializedTrack.name}`);
            }
          } catch (error) {
            console.error(`Error loading audio library track ${serializedTrack.name}:`, error);
          }
        }

        // Create the restored track
        const restoredTrack: AudioTrack = {
          id: serializedTrack.id,
          name: serializedTrack.name,
          type: serializedTrack.type,
          audioLibraryId: serializedTrack.audioLibraryId,
          audioFilePath: serializedTrack.audioFilePath,
          audioBuffer,
          gainNode,
          sourceNode: null,
          volume: serializedTrack.volume,
          muted: serializedTrack.muted,
          startTime: serializedTrack.startTime,
          duration: serializedTrack.duration,
          trimmedDuration: serializedTrack.trimmedDuration,
          color: serializedTrack.color,
          fadeIn: serializedTrack.fadeIn,
          fadeOut: serializedTrack.fadeOut,
          isLoaded,
          isPlaying: false,
          brainwaveSettings: serializedTrack.brainwaveSettings,
          brainwaveOscillator: null,
          noiseSettings: serializedTrack.noiseSettings,
          noiseGenerator: null
        };

        restoredTracks.push(restoredTrack);
      }

      // Update tracks state
      setTracks(restoredTracks);

      // Recalculate total duration based on actual loaded tracks
      const newDuration = calculateTotalDuration(restoredTracks);
      setDuration(newDuration);

      console.log(`Successfully imported ${restoredTracks.length} tracks`);
      console.log(`Updated total duration to: ${newDuration}s`);
    } catch (error) {
      console.error('Error importing mix data:', error);
      throw error;
    }
  }, [audioContext, isInitialized, isPlaying, stop, setMasterVolume, setDuration, analyserNode]);

  // Render audio mix to AudioBuffer using OfflineAudioContext
  const renderMixToBuffer = useCallback(async (onProgress?: (progress: number) => void): Promise<AudioBuffer> => {
    if (!audioContext || !isInitialized) {
      throw new Error('Audio context not initialized');
    }

    if (tracks.length === 0 || duration === 0) {
      throw new Error('No tracks to render');
    }

    try {
      console.log('Starting audio mix rendering...', { tracks: tracks.length, duration });
      
      // Create offline audio context with the same settings as the real-time context
      const sampleRate = audioContext.sampleRate;
      const numberOfChannels = 2; // Stereo
      const length = Math.ceil(duration * sampleRate);
      
      const offlineContext = new OfflineAudioContext(numberOfChannels, length, sampleRate);
      
      // Create master gain node for offline context
      const offlineMasterGain = offlineContext.createGain();
      offlineMasterGain.gain.value = masterVolume;
      offlineMasterGain.connect(offlineContext.destination);

      // Process each track
      const loadedTracks = tracks.filter(track => track.isLoaded);
      console.log(`Rendering ${loadedTracks.length} loaded tracks...`);

      // Keep track of noise generator start promises
      const noiseStartPromises: Promise<void>[] = [];

      for (const track of loadedTracks) {
        console.log(`Setting up track: ${track.name} (${track.type})`);
        
        // Create gain node for this track
        const trackGain = offlineContext.createGain();
        const volume = track.muted ? 0 : track.volume;
        trackGain.gain.value = volume;
        trackGain.connect(offlineMasterGain);

        if (track.type === 'brainwave' && track.brainwaveSettings) {
          // Create brainwave oscillator for offline context
          const effectiveDuration = track.trimmedDuration || track.duration;
          const brainwaveOscillator = new BrainwaveOscillatorNode({
            audioContext: offlineContext,
            modulationType: track.brainwaveSettings.modulationType,
            controlPoints: track.brainwaveSettings.controlPoints,
            duration: effectiveDuration,
            trackVolume: volume
          });
          
          brainwaveOscillator.connect(trackGain);
          
          // Calculate timing
          const trackStartTime = track.startTime || 0;
          const trackEndTime = trackStartTime + effectiveDuration;
          
          if (trackStartTime < duration && trackEndTime > 0) {
            brainwaveOscillator.start(trackStartTime, 0);
          }
        } else if (track.type === 'noise' && track.noiseSettings) {
          // Create noise generator for offline context
          const effectiveDuration = track.trimmedDuration || track.duration;
          const noiseGenerator = new WhiteNoiseGeneratorNode(offlineContext, {
            ...track.noiseSettings,
            duration: effectiveDuration
          });
          
          noiseGenerator.connect(trackGain);
          noiseGenerator.setVolume(volume);
          
          // Calculate timing
          const trackStartTime = track.startTime || 0;
          const trackEndTime = trackStartTime + effectiveDuration;
          
          if (trackStartTime < duration && trackEndTime > 0) {
            // Apply fade effects if specified
            if (track.fadeIn && track.fadeIn > 0) {
              noiseGenerator.scheduleFadeIn(track.fadeIn, volume, trackStartTime);
            }

            if (track.fadeOut && track.fadeOut > 0) {
              const fadeOutStart = trackStartTime + effectiveDuration - track.fadeOut;
              noiseGenerator.scheduleFadeOut(track.fadeOut, fadeOutStart, volume);
            }
            
            // For offline rendering, collect the start promise
            const startPromise = noiseGenerator.start(trackStartTime).catch(error => {
              console.error('Failed to start noise generator in offline context:', error);
            });
            noiseStartPromises.push(startPromise);
          }
        } else if (track.audioBuffer) {
          // Create buffer source for regular audio tracks
          const source = offlineContext.createBufferSource();
          source.buffer = track.audioBuffer;
          source.connect(trackGain);

          // Apply fade effects if specified
          if (track.fadeIn && track.fadeIn > 0) {
            trackGain.gain.setValueAtTime(0, track.startTime || 0);
            trackGain.gain.linearRampToValueAtTime(volume, (track.startTime || 0) + track.fadeIn);
          }

          if (track.fadeOut && track.fadeOut > 0) {
            const effectiveDuration = track.trimmedDuration || track.duration;
            const fadeOutStart = (track.startTime || 0) + effectiveDuration - track.fadeOut;
            trackGain.gain.setValueAtTime(volume, fadeOutStart);
            trackGain.gain.linearRampToValueAtTime(0, fadeOutStart + track.fadeOut);
          }

          // Start the source with proper timing
          const startTime = track.startTime || 0;
          const effectiveDuration = track.trimmedDuration || track.duration;
          
          if (track.trimmedDuration && track.trimmedDuration < track.duration) {
            source.start(startTime, 0, track.trimmedDuration);
          } else {
            source.start(startTime, 0);
          }
        }
      }

      // Wait for all noise generators to start
      if (noiseStartPromises.length > 0) {
        console.log(`Waiting for ${noiseStartPromises.length} noise generators to start...`);
        await Promise.all(noiseStartPromises);
      }

      console.log('Starting offline rendering...');
      const renderedBuffer = await offlineContext.startRendering();
      
      console.log('Audio buffer rendered successfully', { 
        duration: renderedBuffer.duration,
        sampleRate: renderedBuffer.sampleRate,
        numberOfChannels: renderedBuffer.numberOfChannels,
        length: renderedBuffer.length
      });
      
      return renderedBuffer;
    } catch (error) {
      console.error('Error rendering audio mix:', error);
      throw error;
    }
  }, [audioContext, isInitialized, tracks, duration, masterVolume]);

  // Function to detect supported audio formats - prioritize AAC, fallback to MP3 with MediaBunny extension
  const detectSupportedAudioFormat = useCallback(async (): Promise<'aac' | 'mp3' | 'unsupported'> => {
    try {
      // Check AAC support first (preferred format)
      const aacSupported = await canEncodeAudio('aac');
      if (aacSupported) {
        console.log('Browser supports AAC encoding - using high-quality AAC/MP4');
        return 'aac';
      }
    } catch (error) {
      console.log('AAC encoding not available:', error);
    }
    
    try {
      // Check native MP3 support first
      const mp3Supported = await canEncodeAudio('mp3');
      if (mp3Supported) {
        console.log('Browser supports native MP3 encoding');
        return 'mp3';
      } else {
        // Register MP3 encoder extension for browsers without native support
        console.log('Registering MediaBunny MP3 encoder extension...');
        registerMp3Encoder();
        console.log('MediaBunny MP3 encoder registered successfully');
        return 'mp3';
      }
    } catch (error) {
      console.error('MP3 encoding setup failed:', error);
      return 'unsupported';
    }
  }, []);

  // Helper function to downsample AudioBuffer to reduce memory usage
  const downsampleAudioBuffer = useCallback((audioBuffer: AudioBuffer, targetSampleRate: number = 44100): AudioBuffer => {
    if (audioBuffer.sampleRate <= targetSampleRate) {
      return audioBuffer; // Already at or below target rate
    }

    const ratio = audioBuffer.sampleRate / targetSampleRate;
    const newLength = Math.round(audioBuffer.length / ratio);
    const newBuffer = audioContext!.createBuffer(audioBuffer.numberOfChannels, newLength, targetSampleRate);

    for (let channel = 0; channel < audioBuffer.numberOfChannels; channel++) {
      const oldData = audioBuffer.getChannelData(channel);
      const newData = newBuffer.getChannelData(channel);
      
      for (let i = 0; i < newLength; i++) {
        const sourceIndex = Math.round(i * ratio);
        newData[i] = oldData[sourceIndex] || 0;
      }
    }

    console.log(`Downsampled audio from ${audioBuffer.sampleRate}Hz to ${targetSampleRate}Hz (${audioBuffer.length} → ${newLength} samples)`);
    return newBuffer;
  }, [audioContext]);

  // Helper function to convert AudioBuffer to MP3 using MediaBunny
  const convertAudioBufferToMP3 = useCallback(async (audioBuffer: AudioBuffer, onProgress?: (progress: number) => void): Promise<Blob> => {
    try {
      console.log('Starting MP3 encoding with MediaBunny...', {
        duration: audioBuffer.duration,
        sampleRate: audioBuffer.sampleRate,
        numberOfChannels: audioBuffer.numberOfChannels
      });

      // Create audio source from the rendered buffer
      const audioSource = new AudioBufferSource({
        codec: 'mp3',
        bitrate: 128000, // 128 kbps for good quality
      });

      // Create buffer target to receive the output
      const target = new BufferTarget();

   
      // Create output configuration for MP3
      const output = new Output({
        format: new Mp3OutputFormat(),
        target,
      });

      // Add audio track
      output.addAudioTrack(audioSource);

      // Track progress with a more realistic estimation
      const startTime = performance.now();
      const audioDuration = audioBuffer.duration;
      let progressInterval: NodeJS.Timeout | null = null;

      // Report initial progress
      if (onProgress) {
        onProgress(0);
      }

      // Start the encoding process
      await output.start();

      // Set up progress tracking based on encoding speed
      const estimatedEncodingTime = (audioDuration * 1000) / 50; // Assume 50x real-time speed
      
      progressInterval = setInterval(() => {
        const elapsed = performance.now() - startTime;
        const progress = Math.min(elapsed / estimatedEncodingTime, 0.95); // Cap at 95% until finalize
        
        if (onProgress) {
          onProgress(Math.round(progress * 100));
        }
      }, 50); // Update every 50ms for smooth progress

      // Add the AudioBuffer directly to the source
      await audioSource.add(audioBuffer);

      console.log('Finalizing output...');
      // Finalize the output
      await output.finalize();

      // Clear progress interval
      if (progressInterval) {
        clearInterval(progressInterval);
      }
      
      // Report completion
      if (onProgress) {
        onProgress(100);
      }

      // Get the result - target.buffer might be null, so check first
      const resultBuffer = target.buffer;
      if (!resultBuffer) {
        throw new Error('No output buffer available after encoding');
      }
      
      const blob = new Blob([resultBuffer], { type: 'audio/mpeg' });

      const encodingTime = (performance.now() - startTime) / 1000;
      const speedFactor = audioDuration / encodingTime;
      
      console.log(`MP3 encoding complete: ${(blob.size / 1024 / 1024).toFixed(2)} MB (from ${(audioBuffer.length * 4 / 1024 / 1024).toFixed(2)} MB raw)`);
      console.log(`Encoding took ${encodingTime.toFixed(2)}s for ${audioDuration.toFixed(2)}s of audio (${speedFactor.toFixed(1)}x real-time)`);
      
      return blob;
    } catch (error) {
      console.error('Error encoding to MP3:', error);
      throw error;
    }
  }, []);


  // Helper function to convert AudioBuffer to AAC/MP4 blob using Mediabunny
  const convertAudioBufferToAAC = useCallback(async (audioBuffer: AudioBuffer, onProgress?: (progress: number) => void): Promise<Blob> => {
    try {
      console.log('Starting AAC/MP4 encoding with Mediabunny...', {
        duration: audioBuffer.duration,
        sampleRate: audioBuffer.sampleRate,
        numberOfChannels: audioBuffer.numberOfChannels
      });

      // Create audio source from the rendered buffer
      const audioSource = new AudioBufferSource({
        codec: 'aac',
        bitrate: 128000, // 128 kbps for high quality
      });

      // Create buffer target to receive the output
      const target = new BufferTarget();

      // Create output configuration for MP4 container with AAC codec
      const output = new Output({
        format: new Mp4OutputFormat(),
        target,
      });

      // Add audio track
      output.addAudioTrack(audioSource);

      // Track progress with a more realistic estimation
      const startTime = performance.now();
      const audioDuration = audioBuffer.duration;
      let progressInterval: NodeJS.Timeout | null = null;

      // Report initial progress
      if (onProgress) {
        onProgress(0);
      }

      // Start the encoding process
      await output.start();

      // Set up progress tracking based on encoding speed
      const estimatedEncodingTime = (audioDuration * 1000) / 100; // Assume 100x real-time speed
      
      progressInterval = setInterval(() => {
        const elapsed = performance.now() - startTime;
        const progress = Math.min(elapsed / estimatedEncodingTime, 0.95); // Cap at 95% until finalize
        
        if (onProgress) {
          onProgress(Math.round(progress * 100));
        }
      }, 50); // Update every 50ms for smooth progress

      // Add the AudioBuffer directly to the source
      await audioSource.add(audioBuffer);

      // Finalize the output
      await output.finalize();

      // Clear progress interval
      if (progressInterval) {
        clearInterval(progressInterval);
      }
      
      // Report completion
      if (onProgress) {
        onProgress(100);
      }

      // Get the result - target.buffer might be null, so check first
      const resultBuffer = target.buffer;
      if (!resultBuffer) {
        throw new Error('No output buffer available after encoding');
      }
      
      const blob = new Blob([resultBuffer], { type: 'audio/mp4' });

      const encodingTime = (performance.now() - startTime) / 1000;
      const speedFactor = audioDuration / encodingTime;
      
      console.log(`AAC/MP4 encoding complete: ${(blob.size / 1024 / 1024).toFixed(2)} MB (from ${(audioBuffer.length * 4 / 1024 / 1024).toFixed(2)} MB raw)`);
      console.log(`Encoding took ${encodingTime.toFixed(2)}s for ${audioDuration.toFixed(2)}s of audio (${speedFactor.toFixed(1)}x real-time)`);
      
      return blob;
    } catch (error) {
      console.error('Error encoding to AAC/MP4:', error);
      throw error;
    }
  }, []);
  
  /**
   * Combined function to render and encode with automatic format detection
   * 
   * Priority order:
   * 1. AAC (best quality, widely supported, smallest files)
   * 2. MP3 (universal compatibility, good compression via wasm-media-encoders)
   * 3. Unsupported - shows message directing users to Chrome
   * 
   * Uses canEncodeAudio API for AAC, wasm-media-encoders for MP3
   */
  const renderMixToFile = useCallback(async (onProgress?: (stage: 'rendering' | 'encoding', progress: number) => void): Promise<Blob> => {
    // Step 1: Render to AudioBuffer
    if (onProgress) {
      onProgress('rendering', 0);
    }
    
    const audioBuffer = await renderMixToBuffer();
    
    if (onProgress) {
      onProgress('rendering', 100);
    }
    
    // Step 2: Detect supported format and encode accordingly
    const supportedFormat = await detectSupportedAudioFormat();
    console.log(`Using audio format: ${supportedFormat}`);
    
    if (supportedFormat === 'unsupported') {
      throw new Error(
        'Unable to encode audio in this browser. For the best audio finalization experience, please open this project in an up-to-date version of Chrome or Edge.'
      );
    }
    
    let encodedBlob: Blob;
    
    if (supportedFormat === 'aac') {
      console.log('Converting to AAC/MP4...');
      encodedBlob = await convertAudioBufferToAAC(audioBuffer, (progress) => {
        if (onProgress) {
          onProgress('encoding', progress);
        }
      });
    } else {
      console.log('Converting to MP3...');
      // Downsample to 44.1kHz for optimal compatibility and file size
      const processedBuffer = downsampleAudioBuffer(audioBuffer, 44100);
      encodedBlob = await convertAudioBufferToMP3(processedBuffer, (progress) => {
        if (onProgress) {
          onProgress('encoding', progress);
        }
      });
    }
    
    console.log('Audio mix complete!', {
      duration: audioBuffer.duration,
      format: supportedFormat,
      size: (encodedBlob.size / 1024 / 1024).toFixed(2) + ' MB'
    });
    
    return encodedBlob;
  }, [renderMixToBuffer, detectSupportedAudioFormat, convertAudioBufferToAAC, convertAudioBufferToMP3, downsampleAudioBuffer]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (timeUpdateIntervalRef.current) {
        clearInterval(timeUpdateIntervalRef.current);
      }
      if (audioContext) {
        audioContext.close();
      }
    };
  }, [audioContext]);

  return {
    // State
    audioContext,
    masterGainNode,
    analyserNode,
    isInitialized,
    isPlaying,
    currentTime,
    duration,
    tracks,
    masterVolume,
    isLooping,

    // Controls
    initializeAudio,
    addTrack,
    loadTrackAudio,
    loadTrackAudioBuffer,
    removeTrack,
    updateTrack,
    setMasterVolume,
    play,
    pause,
    stop,
    seek,
    toggleLoop,
    getFrequencyData,
    getWaveformData,
    
    // Mix management
    exportMixData,
    importMixData,
    renderMixToFile
  };
};